
import React, { useState, useRef, useEffect } from "react";
import { Trash, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { v4 as uuidv4 } from "uuid";
import { ProductAttribute } from "@/types/attributes";
import { getUserProducts } from "@/services/products.service";
import { useQuery } from "@tanstack/react-query";
import logger from "@/utils/logger";
import { useThemeConfig } from "@/components/ui/theme-config-provider";

interface TagSelectorProps {
  attributes: ProductAttribute[];
  onAttributeAdd: (attribute: ProductAttribute) => void;
  onAttributeRemove: (attributeId: string) => void;
  suggestedAttributes?: ProductAttribute[];
  autoFocus?: boolean;
}


const TagSelector: React.FC<TagSelectorProps> = ({
  attributes = [],
  onAttributeAdd,
  onAttributeRemove,
  suggestedAttributes = [],
  autoFocus = false
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useThemeConfig();

  const { data: products = [] } = useQuery({
    queryKey: ["user-products"],
    queryFn: getUserProducts
  });
  

  // Collect unique attributes from all products
  const allProductAttributes = React.useMemo(() => {
    const uniqueAttrs: Record<string, ProductAttribute> = {};
    
    products.forEach(product => {
      if (Array.isArray(product.attributes)) {
        product.attributes.forEach(attr => {
          uniqueAttrs[attr.label] = attr;
        });
      }
    });
    
    return Object.values(uniqueAttrs);
  }, [products]);

  // Combine suggested attributes with existing ones from all products
  const availableSuggestions = React.useMemo(() => {
    const allSuggestions = Array.isArray(suggestedAttributes) && suggestedAttributes.length > 0
      ? suggestedAttributes
      : [];
      logger.log("🧪 Fallback to allProductAttributes:", allProductAttributes.map(a => a.label));


      const uniqueSuggestions: Record<string, ProductAttribute> = {};
    
      
    allSuggestions.forEach(attr => {
      // Only add if not already in the list
      const isDuplicate = attributes.some(
        a =>
          (a.id && attr.id && a.id === attr.id) ||
          (a.label && attr.label && a.label.toLowerCase() === attr.label.toLowerCase())
      );
      
      if (!isDuplicate) {
        uniqueSuggestions[attr.label] = attr;
      }
      
    });
    
    return Object.values(uniqueSuggestions);
  }, [suggestedAttributes, allProductAttributes, attributes]);
  
  // Filter suggestions based on input
  const filteredSuggestions = React.useMemo(() => {
    logger.log("🔍 TagSelector: suggestedAttributes", suggestedAttributes);
    logger.log("🔍 TagSelector: attributes (attivi)", attributes);
    logger.log("🔍 TagSelector: availableSuggestions (filtrate)", availableSuggestions.map(a => a.label));

    return availableSuggestions.filter(attr => 
      attr.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [availableSuggestions, inputValue]);

  const handleAddAttribute = () => {
    if (!inputValue.trim()) return;

    const newAttribute: ProductAttribute = {
      id: uuidv4(),
      label: inputValue.trim()
    };

    onAttributeAdd(newAttribute);
    setInputValue("");
    setShowSuggestions(false);
    
    // Focus the input after adding
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddAttribute();
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    } else if (e.key === "ArrowDown" && showSuggestions && filteredSuggestions.length > 0) {
      // Handle down arrow key to navigate suggestions
      // Would need additional state to track selected suggestion
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Set focus when component mounts if autoFocus is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Use a small timeout to ensure the focus happens after React rendering
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const selectSuggestion = (suggestion: ProductAttribute) => {
    onAttributeAdd(suggestion);
    setInputValue("");
    setShowSuggestions(false);
    
    // Focus the input after selection
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <div className="flex flex-wrap gap-1 mb-2">
        {attributes.map((attr) => (
          <span
            key={attr.id}
            className="inline-flex items-center px-2 py-1 rounded-md text-xs"
            style={{ backgroundColor: theme.backgroundColor, color: theme.primaryColor }}
          >
            {attr.label}
            <button
              type="button"
              onClick={() => onAttributeRemove(attr.id)}
              aria-label={`Rimuovi ${attr.label}`}
              className="ml-1 opacity-70 hover:opacity-100"
              style={{ color: theme.primaryColor,
                backgroundColor: theme.backgroundColor,
                borderRadius: '50%',
                padding: '0.25rem'
               }}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      <div className="relative">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyPress}
            placeholder="Aggiungi un attributo..."
            className="flex-1"
            style={{ color: theme.primaryColor,
                backgroundColor: theme.backgroundColor, 
                padding: '0.25rem'
               }}
            
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleAddAttribute}
            disabled={!inputValue.trim()}
            style={{ color: theme.textPrimaryColor,
                backgroundColor: theme.primaryColor,
                borderRadius: '25%',
                padding: '0.25rem'
               }}
            aria-label="Aggiungi attributo"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div
            className="absolute z-20 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto border"
            style={{
              backgroundColor: theme.backgroundColor,
              color: theme.primaryColor,
              borderColor: theme.borderColor,
            }}
          >
            {filteredSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                onClick={() => selectSuggestion(suggestion)}
                className="px-4 py-2 text-sm cursor-pointer hover:opacity-80"
                style={{ backgroundColor: theme.backgroundColor, color: theme.primaryColor }}
              >
                {suggestion.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagSelector;
